import { QueryFunctionContext } from '@tanstack/react-query'
import { DELETE, GET, POST, PUT } from '../../../redux/middleware/types'
import backendRequest from '../../../services/api/backendRequest'
import dimensionEndpoints from '../api/dimensionEndpoints'
import dimensionQueryKeys from '../queries/dimensionQueryKeys'
import { Dimension, UndoRequestResponse } from '../types/Dimension'

export const fetchDimensions = async ({
  queryKey: [{ companyId }],
  signal
}: QueryFunctionContext<ReturnType<(typeof dimensionQueryKeys)['list']>>): Promise<Dimension[]> => {
  if (!companyId) return []
  const dimensions = await backendRequest<Dimension[]>({
    method: GET,
    url: dimensionEndpoints.list(companyId),
    signal
  })
  // Backend doesn't guarantee `order` sequencing in the GET response. Every consumer (incl.
  // ag-grid's treeData sibling ordering, which has no concept of an `order` field and just
  // renders rowData in array order) relies on this array already being sorted, so sort once
  // here rather than in every individual selector.
  return dimensions.slice().sort((a, b) => a.order - b.order)
}

export const fetchUndo = async <Entity>({
  queryKey: [{ companyId }],
  signal
}: QueryFunctionContext<ReturnType<(typeof dimensionQueryKeys)['undo']>>): Promise<UndoRequestResponse<Entity>[]> => {
  return backendRequest<UndoRequestResponse<Entity>[]>({
    method: GET,
    url: dimensionEndpoints.undo(companyId),
    signal
  })
}

export const undoLatest = async (companyId: string): Promise<Dimension> => {
  return backendRequest<Dimension>({
    method: POST,
    url: dimensionEndpoints.latest(companyId)
  })
}

export const createDimension = async (data: Dimension, companyId: string): Promise<Dimension> => {
  return backendRequest<Dimension>({
    method: POST,
    url: dimensionEndpoints.list(companyId),
    data
  })
}

export const updateDimension = async (data: Dimension, companyId: string): Promise<Dimension> => {
  return backendRequest<Dimension>({
    method: PUT,
    url: dimensionEndpoints.detail(companyId, data.dimensionId),
    data
  })
}

export const updateDimensions = async (data: Dimension[], companyId: string): Promise<Dimension[]> => {
  return backendRequest<Dimension[]>({
    method: PUT,
    url: dimensionEndpoints.list(companyId),
    data
  })
}

export const updateDefaultDimension = async (
  companyId: string,
  dimensionId: Dimension['dimensionId']
): Promise<Dimension> => {
  return backendRequest<Dimension>({
    method: PUT,
    url: dimensionEndpoints.defaultDimension(companyId, dimensionId)
  })
}

export const deleteDefaultDimension = async (
  companyId: string,
  dimensionId: Dimension['dimensionId']
): Promise<Dimension> => {
  return backendRequest<Dimension>({
    method: DELETE,
    url: dimensionEndpoints.defaultDimension(companyId, dimensionId)
  })
}

export const deleteDimension = async (companyId: string, dimensionId: Dimension['dimensionId']): Promise<Dimension> => {
  return backendRequest<Dimension>({
    method: DELETE,
    url: dimensionEndpoints.detail(companyId, dimensionId)
  })
}

export const deleteDimensions = async (
  dimensionIds: Dimension['dimensionId'][],
  companyId: string
): Promise<Dimension[]> => {
  return backendRequest<Dimension[]>({
    method: DELETE,
    url: dimensionEndpoints.list(companyId),
    data: dimensionIds
  })
}
